import { SyntheticEvent, useCallback, useContext, useMemo } from "react";

import { LocalStorageKeys } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useConfiguration } from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  getPrimaryAttributesWithSelectedSearchTerms,
  getTranslatedValue,
} from "@is/utils/attributes";

const useManageSearchHeader = () => {
  const { searchTerms, updateViewState } = useContext(ViewStateContext).content;

  const { attributes, loyaltySignupForm } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const countryCode = LocalStorageService.getItem(LocalStorageKeys.CountryCode);

  const showSignUpButton = useMemo(() => {
    const allowedCountries = loyaltySignupForm?.countries ?? [];

    const isAllowedInSelectedCountry =
      allowedCountries.length === 0 || allowedCountries.includes(countryCode);

    return loyaltySignupForm?.isEnabled && isAllowedInSelectedCountry;
  }, [countryCode, loyaltySignupForm]);

  const signUpButtonTitle = useMemo(
    () =>
      getTranslatedValue(loyaltySignupForm?.title?.translations || []) ??
      loyaltySignupForm?.title?.value ??
      "",
    [loyaltySignupForm]
  );

  const primaryAttributes = useMemo(
    () => attributes.filter((attr) => attr.primary),
    [attributes]
  );

  const primaryAttributesWithOptions = useMemo(
    () =>
      getPrimaryAttributesWithSelectedSearchTerms(
        primaryAttributes,
        searchTerms
      ),
    [primaryAttributes, searchTerms]
  );

  const handleAttributeClick = useCallback(
    (e: SyntheticEvent) => {
      const selectedTab = e.currentTarget.getAttribute("data-name");
      if (selectedTab) {
        updateViewState({ selectedTab });
      }
    },
    [updateViewState]
  );

  const handleToggleLanguageAndCurrencyModal = useCallback(() => {
    updateViewState({
      showLanguageAndCurrencyModal: true,
    });

    trackEvent({
      eventType: TrackingEventTypes.LanguageAndCurrencyModalOpen,
      data: {},
    });
  }, [updateViewState]);

  const handleToggleSignUpModal = useCallback(() => {
    updateViewState({
      showSignUpModal: true,
    });
  }, [updateViewState]);

  return {
    attributes: primaryAttributesWithOptions?.filter(
      (attribute) => attribute.visible
    ),
    handleAttributeClick,
    handleToggleLanguageAndCurrencyModal,
    handleToggleSignUpModal,
    showSignUpButton,
    signUpButtonTitle,
  };
};

export default useManageSearchHeader;
